/* eslint-disable no-unused-vars */
import { useState, useRef } from 'react';
import { Buttons, ParagraphWithTooltip, DownloadProgress, IconButton } from 'components';
import { DeleteIconNew, ErrorMarker } from 'assets/icons';
import * as S from './FileUploader.styled';
import { parseAPKName } from 'utils';
import axios from 'axios';
import { saveAs } from 'file-saver';

const isNotFile = (selectedFile) => {
  if (typeof selectedFile !== 'object') return true;
  return false;
};

const FileUploader = ({
  notMoreSizeFile = 60,
  availableTypes = ['text/csv'],
  accept = '.csv',
  onChange,
  error,
  errorText,
  alertMessage = `Please select a CSV file up to 60 MB in size.`,
  value,
  btnName = 'Browse for Files',
  setIsSaveDisabled = () => {},
}) => {
  const [selectedFile, setSelectedFile] = useState(value);
  const [isDragging, setIsDragging] = useState(false);

  const [progress, setProgress] = useState(0);
  const cancelTokenSource = useRef(null);

  const fileInputRef = useRef(null);

  const checkFile = (file) => {
    if (file) {
      const extensionType = file.name.split('.')[file.name.split('.').length - 1].toLowerCase();
      const typeIsAvailable = availableTypes.some((type) => type === extensionType);
      if (typeIsAvailable && file.size <= notMoreSizeFile * 1024 * 1024) {
        setSelectedFile(file);
        onChange(file);
        setIsSaveDisabled(false);
      } else {
        showAlert(alertMessage);
      }
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    checkFile(file);
  };

  const handleFileDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    checkFile(file);
  };

  const handleFileRemove = () => {
    setSelectedFile(null);
    onChange(null);
    setIsSaveDisabled(true);
  };

  const handleBrowseClick = (event) => {
    event.preventDefault();
    fileInputRef.current.value = null;
    fileInputRef.current.click();
  };

  const showAlert = (message) => {
    alert(message);
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const downloadFile = async () => {
    try {
      // Create a cancellation source
      cancelTokenSource.current = axios.CancelToken.source();

      const response = await axios.get(selectedFile, {
        responseType: 'blob', // To download files
        onDownloadProgress: (progressEvent) => {
          const { progress } = progressEvent;
          setProgress(() => progress);
        },
        cancelToken: cancelTokenSource.current.token,
      });
      // Using FileSaver.js to save a file
      const blob = new Blob([response.data]);
      saveAs(blob, parseAPKName(selectedFile)); // Replace your file name
      setProgress(() => 100);
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log('Request canceled: ', error.message);
      } else {
        console.error('Error downloading file: ', error);
      }
    } finally {
      setProgress(() => 0);
    }
  };

  const cancelDownload = () => {
    if (cancelTokenSource.current) {
      cancelTokenSource.current.cancel('The download was canceled by the user.');
      setProgress(0);
    }
  };

  return (
    <>
      {!selectedFile ? (
        <S.Container>
          <S.FileUploaderWrapper
            error={error}
            onDrop={handleFileDrop}
            onDragOver={(event) => event.preventDefault()}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            isDragging={isDragging}
          >
            <S.FileInput
              type='file'
              id='fileInput'
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept={accept}
            />
            <Buttons.SecondaryNormal id='upload_csv' onClick={handleBrowseClick}>
              {btnName}
            </Buttons.SecondaryNormal>
            <span>or Drag and drop files here</span>
          </S.FileUploaderWrapper>
          {error && (
            <S.ErrorWrapper>
              <ErrorMarker />
              <S.ErrorText>{errorText}</S.ErrorText>
            </S.ErrorWrapper>
          )}
        </S.Container>
      ) : (
        <S.SelectedFileWrapper>
          <S.FileItem isFile={isNotFile(selectedFile) && !progress > 0}>
            <div
              style={{ width: '90%' }}
              onClick={isNotFile(selectedFile) && !progress > 0 ? downloadFile : () => {}}
            >
              {progress > 0 ? (
                <DownloadProgress percentage={progress * 100} width={20} height={20} />
              ) : (
                <ParagraphWithTooltip text={parseAPKName(selectedFile)} />
              )}
            </div>
            <IconButton.SmallSize
              onClick={handleFileRemove}
              icon={DeleteIconNew}
              type={IconButton.btnTypes.internal}
              style={{ marginLeft: 'auto' }}
            />
          </S.FileItem>
        </S.SelectedFileWrapper>
      )}
    </>
  );
};

export default FileUploader;
